import { ADVANCED_SETTINGS_TAB_INDEX } from "@constants/index";
import classNames from "classnames";
import React from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Button, Spinner, TabContent, TabPane } from "reactstrap";

function TabBodyErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
    const { t } = useTranslation();
    const isChunkLoadError =
        error?.name === "ChunkLoadError" ||
        /Loading chunk [\d]+ failed/i.test(error?.message ?? "");

    return (
        <div
            role="alert"
            className="d-flex flex-column justify-content-center align-items-center h-100 p-4"
            style={{ gap: "12px" }}
        >
            <p className="m-0">
                {isChunkLoadError
                    ? t(
                          "A new version is available. Please reload the page to continue."
                      )
                    : t("There's an error.")}
            </p>
            <Button
                data-testid={
                    isChunkLoadError
                        ? "modal-content-error-reload-btn"
                        : "modal-content-error-retry-btn"
                }
                outline
                color="primary"
                onClick={
                    isChunkLoadError
                        ? () => window.location.reload()
                        : resetErrorBoundary
                }
            >
                {isChunkLoadError ? t("Reload") : t("Try again")}
            </Button>
        </div>
    );
}

export const TabBody = React.memo(function TabBody(
    props: TabBodyProps
): JSX.Element {
    const {
        tabs,
        activeTab,
        activeSubTab,
        isSubMenuCollapsed,
        isWaysTabActive,
    } = props;

    const panelRefs = React.useRef<Map<string, HTMLElement>>(new Map());
    const isFirstRender = React.useRef(true);

    const activePanelId = React.useMemo(() => {
        const tab = tabs[activeTab];
        if (!tab) return null;
        if (tab.subMenu !== undefined && isSubMenuCollapsed) {
            const sub = tab.subMenu[activeSubTab];
            if (!sub) return null;
            return `panel-${tab.id}-${sub.id}`;
        }
        return `panel-${tab.id}`;
    }, [tabs, activeTab, activeSubTab, isSubMenuCollapsed]);

    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (!activePanelId) return;
        const active = document.activeElement as HTMLElement | null;
        const cameFromTab =
            active?.getAttribute("role") === "tab" &&
            active?.closest('[role="tablist"]') !== null;
        if (!cameFromTab) return;
        const panel = panelRefs.current.get(activePanelId);
        panel?.focus({ preventScroll: true });
    }, [activePanelId]);

    const registerRef = (id: string) => (node: HTMLElement | null) => {
        if (node) {
            panelRefs.current.set(id, node);
        } else {
            panelRefs.current.delete(id);
        }
    };

    return (
        <TabContent
            activeTab={activeTab}
            className={classNames({
                "px-5":
                    !isWaysTabActive &&
                    activeTab !== ADVANCED_SETTINGS_TAB_INDEX,
            })}
            style={{
                overflowY: isWaysTabActive ? "hidden" : "unset",
            }}
        >
            <ErrorBoundary FallbackComponent={TabBodyErrorFallback}>
                <React.Suspense
                    fallback={
                        <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner color="primary" />
                        </div>
                    }
                >
                    {tabs.map((element, index) =>
                        element.subMenu !== undefined && isSubMenuCollapsed ? (
                            element.subMenu?.map((subItem, subMenuIndex) => {
                                const isPaneActive =
                                    activeTab === index &&
                                    subMenuIndex === activeSubTab;
                                const subPanelId = `panel-${element.id}-${subItem.id}`;
                                return (
                                    <TabPane
                                        tabId={index}
                                        innerRef={
                                            registerRef(
                                                subPanelId
                                            ) as React.Ref<HTMLDivElement>
                                        }
                                        role="tabpanel"
                                        id={subPanelId}
                                        aria-labelledby={`tab-${element.id}-${subItem.id}`}
                                        tabIndex={0}
                                        className={classNames({
                                            "d-none":
                                                subMenuIndex !== activeSubTab,
                                        })}
                                        style={{
                                            height: "100%",
                                            width: "100%",
                                            overflow: "hidden",
                                        }}
                                        key={subItem.id}
                                    >
                                        {isPaneActive ? subItem.content : null}
                                    </TabPane>
                                );
                            })
                        ) : (
                            <TabPane
                                tabId={index}
                                key={element.id}
                                innerRef={
                                    registerRef(
                                        `panel-${element.id}`
                                    ) as React.Ref<HTMLDivElement>
                                }
                                role="tabpanel"
                                id={`panel-${element.id}`}
                                aria-labelledby={`tab-${element.id}`}
                                tabIndex={0}
                                className={classNames("h-100", {
                                    "design-active-tab__clz":
                                        activeTab ===
                                        ADVANCED_SETTINGS_TAB_INDEX,
                                })}
                            >
                                {activeTab === index ? element.content : null}
                            </TabPane>
                        )
                    )}
                </React.Suspense>
            </ErrorBoundary>
        </TabContent>
    );
});
